import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);
      formBody.append('to', 'adrian@borventures.com');

      const response = await fetch('https://readdy.ai/api/form/d5hsca3gekujqjrtsq40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 px-4 md:px-12 min-h-screen flex items-center">
      <div className="max-w-[700px] mx-auto w-full">
        <div className="mb-12 text-center">
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Let's Connect
          </h2>

          <p 
            className="text-lg md:text-xl text-gray-600 max-w-[680px] mx-auto"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            We're always open to conversations with exceptional founders building in the pet space. Reach out for investment discussions or partnership opportunities.
          </p>
        </div>

        <form 
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="p-8 md:p-12 rounded-3xl"
          style={{
            background: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(0, 212, 255, 0.15)'
          }}
        >
          <div className="space-y-6">
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full h-14 px-5 rounded-xl text-base transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-cyan-400/10"
                style={{
                  background: 'rgba(250, 250, 252, 0.8)',
                  border: '1.5px solid rgba(74, 74, 94, 0.15)',
                  fontFamily: "'Inter', sans-serif"
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#00D4FF';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 74, 94, 0.15)';
                }}
              />
            </div>

            <div>
              <input
                type="email"
                name="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full h-14 px-5 rounded-xl text-base transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-cyan-400/10"
                style={{
                  background: 'rgba(250, 250, 252, 0.8)',
                  border: '1.5px solid rgba(74, 74, 94, 0.15)',
                  fontFamily: "'Inter', sans-serif"
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#00D4FF';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 74, 94, 0.15)';
                }}
              />
            </div>

            <div>
              <textarea
                name="message"
                placeholder="Tell us about your venture or inquiry..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                maxLength={500}
                rows={6}
                className="w-full px-5 py-4 rounded-xl text-base resize-y transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-cyan-400/10"
                style={{
                  background: 'rgba(250, 250, 252, 0.8)',
                  border: '1.5px solid rgba(74, 74, 94, 0.15)',
                  fontFamily: "'Inter', sans-serif"
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#00D4FF';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 74, 94, 0.15)';
                }}
              />
              <p className="text-xs text-gray-500 mt-2 text-right">
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-14 rounded-xl text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(124,58,237,0.3)] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #00D4FF 0%, #7C3AED 100%)',
                fontFamily: "'Inter', sans-serif"
              }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
                Thank you! Your message has been sent successfully.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                Please fill in all fields correctly.
              </div>
            )}
          </div>
        </form>

        <div className="mt-24 pt-12 border-t border-gray-200/50">
          <p 
            className="text-sm text-gray-400 text-center tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.5px' }}
          >
            © 2026 Bor Ventures · Asia's First Pet Innovation Investor
          </p>
          <div className="text-center mt-4">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-purple-600 transition-colors duration-300"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
